// Builds the PUT body for a customer edit.
//
// The edit drawer holds a copy of the whole customer and every input writes
// back into it, so submitting the form wholesale sends eight fields whatever
// the admin actually touched. Two of them do not survive that round trip:
//
//   * `is_marketing_opted_in` — rows predating the column's default are NULL,
//     and the form's `|| false` wrote `false` over them.
//   * `custom_fields` — the drawer seeds every store field with "" so it can
//     render an input for it, and that seeded blob went back to the server.
//
// Both are tracked by the backend's customer.updated webhook, so a pure point
// adjustment dispatched a spurious customer.updated to the merchant's endpoint,
// and the seeded blanks were written into the jsonb column for good.
//
// Sending only what changed also means a profile-only edit carries no `point`,
// which is what stops customers#update from reading the balance back as a
// purchase on discount cards.
const isBlank = (value) =>
  value === null || value === undefined || value === "";

// Blank and absent are the same thing to the API: a cleared text input submits
// "" where the record holds null, and neither is a change worth sending.
const sameText = (a, b) => (isBlank(a) && isBlank(b) ? true : a === b);

const TEXT_FIELDS = ["name", "email", "phone", "phone_country_code", "birthday"];

// The card expiry date needs its own comparison. The record carries a full
// timestamp ("2029-07-01T00:00:00.000Z") while the date input holds a day
// ("2029-07-01"), so a text comparison reports a change on every save and would
// rebuild the wallet pass — and push a notification to the customer — for an
// edit nobody made.
const sameDay = (a, b) => {
  if (isBlank(a) && isBlank(b)) return true;
  if (isBlank(a) || isBlank(b)) return false;

  return String(a).slice(0, 10) === String(b).slice(0, 10);
};

// Drop keys the drawer only invented to render an input for them. A key already
// on the record stays even when blank, so clearing a filled field still saves.
const pruneSeededBlanks = (edited, original) =>
  Object.fromEntries(
    Object.entries(edited).filter(
      ([key, value]) =>
        !isBlank(value) || Object.prototype.hasOwnProperty.call(original, key),
    ),
  );

const sameFields = (a, b) => {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(b, key) && sameText(a[key], b[key]),
  );
};

export function buildCustomerUpdatePayload(original, edited) {
  if (!original || !edited) return {};

  const payload = {};

  TEXT_FIELDS.forEach((field) => {
    if (!sameText(original[field], edited[field])) {
      payload[field] = edited[field] || null;
    }
  });

  // NULL and false are indistinguishable to every reader of this column, so
  // only a real toggle counts.
  if (Boolean(original.is_marketing_opted_in) !== Boolean(edited.is_marketing_opted_in)) {
    payload.is_marketing_opted_in = Boolean(edited.is_marketing_opted_in);
  }

  const editedFields = pruneSeededBlanks(
    edited.custom_fields || {},
    original.custom_fields || {},
  );
  if (!sameFields(editedFields, original.custom_fields || {})) {
    payload.custom_fields = editedFields;
  }

  // Cleared means "this card no longer expires", which is a real edit and has
  // to reach the server as an explicit null rather than being pruned away.
  if (!sameDay(original.expires_at, edited.expires_at)) {
    payload.expires_at = edited.expires_at || null;
  }

  // An emptied number input parses to NaN. The old form coerced that to 0,
  // which silently zeroed the balance; a balance is only ever set deliberately,
  // so an unreadable value means "no adjustment".
  const point = Number(edited.point);
  if (Number.isFinite(point) && point !== Number(original.point || 0)) {
    payload.point = point;
  }

  return payload;
}
